import * as React from "react";
import { Box, Link, Typography } from "@mui/material";

export default function OrganizationsDoc({ onNavigate }) {
  return (
    <Box id="organizations" sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Organizations (Admin vs portal)
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Use this when a customer asks how their organization gets into the system, who can
        change what, and what they should do in the member portal instead of calling you.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 2, mb: 1 }}>
        In Admin (internal team only)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>Where:</strong> Dashboard → <strong>Organizations</strong> (or{" "}
        <strong>/dashboard/organizations</strong>).
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
        <li>
          <strong>Create an organization</strong> — Click <strong>Create Organization</strong>.
          You need a legal name, formation state, a full 9-digit EIN, and whether the record
          should be active. After saving, you land on that organization&apos;s detail page.
        </li>
        <li>
          <strong>Manage an organization</strong> — Open it from the list. Tabs include:{" "}
          <strong>Organization Details</strong> (full profile), <strong>User Access</strong>{" "}
          (who can log in and their role), <strong>Contacts</strong>, <strong>Orders</strong>,{" "}
          <strong>Subscriptions</strong>, and <strong>Registered Agent</strong>. Internal tools
          here are broader than what a customer sees in the portal.
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        In the member portal (customers)
      </Typography>
      <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
        <li>
          <strong>They cannot create an organization</strong> in the portal. Someone internal
          creates the org in Admin (above), then links the right people with{" "}
          <strong>User Access</strong> so they can sign in.
        </li>
        <li>
          <strong>They only see organizations they belong to.</strong> If they don&apos;t see
          their org, they need to be added to that org (Admin → organization → User Access),
          not given a new login by themselves.
        </li>
        <li>
          <strong>Who can edit:</strong> If their role on that org is{" "}
          <strong>Administrator</strong> or <strong>Editor</strong>, they can change the areas
          that show an <strong>Edit</strong> button (for example basic organization info and
          contacts). If they are a <strong>Viewer</strong> (or similar), the portal is mostly{" "}
          <strong>read-only</strong> for those sections.
        </li>
        <li>
          <strong>What they still use the portal for:</strong> Viewing or updating allowed
          profile and contact information, compliance-related info where the product allows
          it, and <strong>Subscriptions</strong> (for example cancelling certain plans when
          the app shows that option). They do <strong>not</strong> have the full Admin view of
          all orders, all users in the system, or internal Squarespace tools.
        </li>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} paragraph>
        <strong>Quick support line:</strong> “New orgs are set up in <strong>Admin →
        Organizations</strong>. Customers work in the <strong>member portal</strong> only for
        orgs they&apos;re already on, and only editors/admins there can change what the buttons
        allow.”
      </Typography>

      {onNavigate && (
        <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/organizations")}
          >
            Go to Organizations →
          </Link>
        </Box>
      )}
    </Box>
  );
}
